import { useUpdateLeadStatus } from "../../api/hooks";
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Button,
  Skeleton,
} from "@pushpress/pushpress-ui";
import { Row } from "@tanstack/react-table";
import { MoreVertical } from "lucide-react";
import { toast } from "react-toastify";
import { queryClient } from "../../../../components/providers/ReactQueryProvider";
import { defaultToastOptions } from "../../../../utils/misc";
import {
  LeadRecordType,
  MemberRecordType,
  PEOPLE_TYPE,
  StaffRecordType,
} from "../../types";

export function LeadRowActions({ row }: { row: Row<LeadRecordType> }) {
  const { mutate: updateLeadStatus, isLoading } = useUpdateLeadStatus();

  const userUuid = row.original.userUuid;
  const status = row.original.leadStatus;
  const disqualifyStatus = "disqualified";

  return (
    <div className="tw-flex tw-justify-end tw-mr-1 max-sm:tw-w-[20px]">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {isLoading ? (
            <Button variant="ghost" data-testid="row-actions-button" disabled>
              <Skeleton className="tw-h-5 tw-w-5" />
            </Button>
          ) : (
            <Button variant="ghost" data-testid="row-actions-button">
              <MoreVertical className="tw-h-5 tw-w-5 max-sm:tw-p-0" />
            </Button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onClick={() => {
              window.location.href = `/members/edit/plan/${userUuid}`;
            }}
          >
            Add plan
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              window.location.href = `/members/lead/${userUuid}`;
            }}
          >
            Edit lead
          </DropdownMenuItem>
          {status !== disqualifyStatus && (
            <DropdownMenuItem
              onClick={() => {
                updateLeadStatus(
                  {
                    userUuid,
                    leadStatus: disqualifyStatus,
                  },
                  {
                    onSuccess: () => {
                      toast.success("Lead status updated", defaultToastOptions);
                      /* Update status in the cache for instant visual update.
                  The data will be re-fetched when the user re-visist the leads tab */
                      queryClient.setQueryData(
                        ["peopleRecords", PEOPLE_TYPE.LEADS],
                        (leadsCachedData: LeadRecordType[]) => {
                          return leadsCachedData.map((lead) =>
                            lead.userUuid === userUuid
                              ? { ...lead, leadStatus: disqualifyStatus }
                              : lead,
                          );
                        },
                      );
                    },
                    onError: () => {
                      toast.error(
                        "There was an error updating the lead status",
                        defaultToastOptions,
                      );
                    },
                  },
                );
              }}
            >
              Disqualify
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export function StaffRowActions({ row }: { row: Row<StaffRecordType> }) {
  const userUuid = row.original.userUuid;
  return (
    <div className="tw-flex tw-justify-end tw-mr-1 max-sm:tw-w-[20px]">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" data-testid="row-actions-button">
            <MoreVertical className="tw-h-5 tw-w-5 max-sm:tw-p-0" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onClick={() => {
              window.location.href = `/users/view/${userUuid}`;
            }}
          >
            View metrics
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              window.location.href = `/users/edit/${userUuid}`;
            }}
          >
            Edit staff info
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              window.location.href = `/members/edit/personal/${userUuid}`;
            }}
          >
            Edit personal info
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              window.location.href = `/reporting/export-payroll`;
            }}
          >
            View payroll report
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export function MemberRowActions({ row }: { row: Row<MemberRecordType> }) {
  return (
    <div className="tw-flex tw-justify-end tw-mr-1 max-sm:tw-w-[20px]">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" data-testid="row-actions-button">
            <MoreVertical className="tw-h-5 tw-w-5 max-sm:tw-p-0" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onClick={() => {
              window.location.href = `/members/edit/personal/${row.original.userUuid}`;
            }}
          >
            Edit personal info
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
